:host {
  /** @prop --select-font-family: Set the font-family of the <anchor-select> */
  --select-font-family: inherit;
  /** @prop --select-background-color: Set the background color of the <anchor-select> */
  --select-background-color: var(--s-color-background-default-default);
  /** @prop --select-background-color-list: Set the background color of the <anchor-select>'s dropdown list, defaults to the same as the select */
  --select-background-color-list: var(--select-background-color);
  /** @prop --select-text-color: Set the text color of the <anchor-select> */
  --select-text-color: var(--s-color-text-default);
  /** @prop --select-border-width: Set the border width of the <anchor-select> */
  --select-border-width: var(--s-size-border-width-default);
  /** @prop --select-border-width-error: Set the border width of the <anchor-select> in case of an error*/
  --select-border-width-error: var(--s-size-border-width-default);
  /** @prop --select-border-radius: Set the border radius of the <anchor-select> */
  --select-border-radius: var(--s-size-radius-medium);
  /** @prop --select-border-color: Set the border color of the <anchor-select> */
  --select-border-color: var(--s-color-border-default);
  /** @prop --select-border-color-hover: Set the hover border color of the <anchor-select> */
  --select-border-color-hover: var(--s-color-outline-default);
  /** @prop --select-border-color-focus: Set the focus border color of the <anchor-select> */
  --select-border-color-focus: var(--s-color-outline-default);
  /** @prop --select-border-color-warning: Set the warning border color of the <anchor-select> */
  --select-border-color-warning: var(--s-color-border-default);
  /** @prop --select-border-color-error: Set the error border color of the <anchor-select> */
  --select-border-color-error: var(--s-color-border-error);
  /** @prop --select-border-color-readonly: Set the readonly border color of the <anchor-select> */
  --select-border-color-readonly: var(--s-color-border-divider);
  /** @prop --select-border-color-disabled: Set the disabled border color of the <anchor-select> */
  --select-border-color-disabled: var(--s-color-border-divider);
  /** @prop --select-z-index: Set the z index of the <anchor-select> dropdown */
  --select-z-index: 1000;
  /** @prop --select-text-color-disabled: Set the text color of the <anchor-select> when disabled */
  --select-text-color-disabled: var(--p-color-stone-600);
  /** @prop --select-text-color-expanded: Set the text color of the <anchor-select> when the dropdown is expanded */
  --select-text-color-expanded: var(--select-text-color-disabled);
  /** @prop --select-message-gap: Set the message gap of the <anchor-select> */
  --select-message-gap: var(--p-size-static-100);
  /** @prop --select-list-offset: Set the offset of the <anchor-select> dropdown list */
  --select-list-offset: calc(var(--s-size-line-height-paragraph) + 2 * var(--p-size-static-150) + 2 * var(--select-border-width));

  /**
   * START DEPRECATED - kept for backwards compatibility - use the above variables instead
   */
  --background-color: var(--select-background-color);
  --text-color: var(--select-text-color);
  --border-width: var(--select-border-width);
  --border-width-error: var(--select-border-width-error);
  --border-color: var(--select-border-color);
  --options-z-index: var(--select-z-index);
  --message-gap: var(--select-message-gap);
  // Disabled variables
  --text-disabled: var(--select-text-color-disabled);
  // Expanded variables
  --main-element-expanded: var(--select-text-color-expanded);
  // Error variables
  --border-error: var(--select-border-color-error);
  /**
   *  !! END DEPRECATED
   */

  display: flex;
  color: var(--select-text-color);
  position: relative;

  &,
  & * {
    box-sizing: border-box;
  }

  .anchor-c-select {
    display: flex;
    flex-direction: column;
    gap: var(--p-size-static-100);
    width: 100%;
    &.expanded {
      margin-bottom: 1px;
    }
    .main-element {
      --option-border-width: var(--select-border-width);
      --option-border-bottom-width: var(--select-border-width);
      --option-border-radius: var(--select-border-radius);
      --option-border-color: var(--select-border-color);
      --option-border-color-hover: var(--select-border-color-hover);
      --option-border-color-focus: var(--select-border-color-focus);
      --option-border-color-warning: var(--select-border-color-warning);
      --option-border-color-error: var(--select-border-color-error);
      --option-border-color-readonly: var(--select-border-color-readonly);
      --option-border-color-disabled: var(--select-border-color-disabled);

      display: flex;
      border-radius: var(--select-border-radius);

      &:hover {
        --option-background-color-hover: var(--option-background-color);
        --option-border-color: var(--option-border-color-hover);
        outline: var(--s-color-outline-aura-hover) solid var(--s-size-outline-aura-width);
      }
      &:not(.expanded):focus,
      &:not(.expanded):focus-visible {
        --option-border-color: var(--option-border-color-focus);
        outline: var(--s-color-outline-default) solid var(--s-size-outline-focus-ring-width);
        outline-offset: var(--s-size-outline-focus-ring-offset);
      }
      &.expanded {
        outline: none;
        --option-border-color: var(--s-color-outline-default);
        --option-border-bottom-width: var(--s-size-border-width-active);
        --option-border-radius: var(--select-border-radius) var(--select-border-radius) 0 0;
      }
      &.error {
        outline: none;
        --option-border-color: var(--select-border-color-error);
      }
      &.disabled {
        --option-border-color: var(--option-border-color-disabled);
        --option-border-width: 0px;
        --option-background-disabled: var(--b-color-neutral-lighter);
        --option-background-pressed: var(--b-color-neutral-lighter);
        --option-color: var(--select-text-color-disabled);
        --option-color-disabled: var(--select-text-color-disabled);
        --option-color-pressed: var(--select-text-color-disabled);
        --option-cursor: not-allowed;
        outline: none;
        &:focus,
        &:focus-visible {
          outline: none;
        }
      }
    }
    &.error {
      border-left: 4px solid var(--select-border-color-error);
      padding-left: 16px;
    }
    &__error-msg {
      display: flex;
      gap: var(--p-size-static-200);
      justify-content: space-between;
      margin: 0 0 var(--p-size-static-100);
      background-color: var(--b-color-error-light);
      padding: var(--p-size-static-100) var(--p-size-static-200);
      border-radius: var(--s-size-radius-medium);
      line-height: var(--p-size-dynamic-300);
    }
  }
  .anchor-c-select__select-items-container {
    position: relative;
    // margin-top: calc(var(--p-size-static-100) * -1);
  }

  .anchor-c-select__select-items {
    position: absolute;
    // margin-top: -1px;
    z-index: var(--select-z-index);
    width: 100%;
    display: flex;
    flex-direction: column;
    max-height: min(300px, 33vh);
    overflow: hidden auto;
    background-color: var(--select-background-color-list);
    anchor-select-item {
      border-top-width: 0;
      --option-background-color: var(--select-background-color-list);
    }
    &.expanded {
      border-bottom-right-radius: var(--select-border-radius);
      border-bottom-left-radius: var(--select-border-radius);
      border: var(--select-border-width) solid var(--s-color-outline-default);
      border-top: none;
    }
  }

  .anchor-c-label,
  ::slotted([slot|='label']) {
    display: block;
    color: var(--select-text-color);
    line-height: var(--p-size-dynamic-300);
  }

  ::slotted(.help-text) {
    margin-top: calc(var(--p-size-static-100) * -1);
    display: inline-block;
    color: var(--select-text-color);
    font-weight: 300;
    line-height: var(--p-size-dynamic-300);
  }

  ::slotted(.label) {
    line-height: var(--s-size-line-height-paragraph);
  }

  .error:not(.expanded) {
    --select-border-width: var(--select-border-width-error);
  }

  .disabled,
  .disabled .anchor-c-label {
    --select-text-color: var(--b-color-neutral-dark);
    --option-border-width: 0;
    color: var(--select-text-color);
  }

  .expanded {
    --select-text-color: var(--select-text-color-expanded);
  }
}
